import { useStudioStore } from "@/store/studio-store";
import {
  isOpenPencilSourceRestoreId,
  openPencilSourceRestoreToken,
} from "./openpencil-source-restore";

export const OPENPENCIL_SOURCE_WORKSPACE_ROUTE_ID = "source-workspace";
export const OPENPENCIL_SOURCE_WORKSPACE_PATH = `/ide/${OPENPENCIL_SOURCE_WORKSPACE_ROUTE_ID}`;

/**
 * The URL of an imported source workspace.
 *
 * `/ide/source-workspace` on its own names NOTHING — it is the same literal
 * for every user and every import, so the link a creator copies out of the
 * address bar cannot reopen their work anywhere else, and a colleague who
 * receives it lands on their own (usually empty) workspace. Appending the
 * import's restore token makes the URL identify the import, which is what
 * lets the source surface bring it back in a browser that has never seen it.
 *
 * The bare path stays valid: an import with no durable server artifact has no
 * token to put here, and minting one anyway would produce a link that
 * resolves to nothing.
 */
export function openPencilSourceWorkspacePath(token?: string | null): string {
  return token && isOpenPencilSourceRestoreId(token)
    ? `${OPENPENCIL_SOURCE_WORKSPACE_PATH}/${encodeURIComponent(token)}`
    : OPENPENCIL_SOURCE_WORKSPACE_PATH;
}

export function isOpenPencilSourceWorkspacePath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, "");
  if (normalized === OPENPENCIL_SOURCE_WORKSPACE_PATH) return true;
  return normalized.startsWith(`${OPENPENCIL_SOURCE_WORKSPACE_PATH}/`);
}

/**
 * The restore token carried by a source-workspace URL, if any. Returns null
 * for the bare route and for any segment that is not a well-formed id, so a
 * hand-edited URL degrades to "no token" instead of reaching the network.
 */
export function readOpenPencilSourceWorkspaceToken(
  pathname: string,
): string | null {
  const normalized = pathname.replace(/\/$/, "");
  if (!normalized.startsWith(`${OPENPENCIL_SOURCE_WORKSPACE_PATH}/`)) return null;
  const raw = normalized.slice(OPENPENCIL_SOURCE_WORKSPACE_PATH.length + 1);
  if (!raw || raw.includes("/")) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return null;
  }
  return isOpenPencilSourceRestoreId(decoded) ? decoded : null;
}

/**
 * Point the authority shell at the Design ▸ Sources task.
 *
 * `setActiveRailItem("source")` alone is not enough: the shell re-derives the
 * rail from the `studioDomain` / `studioView` deep-link params, and when they
 * are absent it falls back to `primary`/`overview` — which is why opening a
 * source-workspace URL landed on the app preview instead of the import the
 * link names. Seeding them makes the link self-sufficient.
 *
 * Only ever seeds; never overwrites. A URL that already carries an explicit
 * view (Tokens, Components) was chosen by the person who shared it, and this
 * must not second-guess it.
 */
function seedDesignSourceDeepLink(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.has("studioDomain") || url.searchParams.has("studioView")) {
    return;
  }
  url.searchParams.set("studioDomain", "design");
  url.searchParams.set("studioView", "sources");
  window.history.replaceState(window.history.state, "", url);
}

export function activateOpenPencilSourceWorkspace(options?: {
  navigate?: boolean;
  replace?: boolean;
}): void {
  const store = useStudioStore.getState();
  store.setStudioMode("edit");
  store.setViewMode("editor");
  store.setActiveRailItem("source");
  store.setView("workspace");

  if (typeof window !== "undefined" && isOpenPencilSourceWorkspacePath(window.location.pathname)) {
    seedDesignSourceDeepLink();
  }

  if (options?.navigate === false || typeof window === "undefined") return;

  // A server project already has a durable home (`studio_projects.settings`),
  // so its own `/ide/{projectId}` URL is the addressable one. Everything else
  // — the unbound `workspace_local` workspace and imported bundles — is only
  // addressable through the import's restore token.
  const target = store.currentProjectId
    ? `/ide/${encodeURIComponent(store.currentProjectId)}`
    : openPencilSourceWorkspacePath(
        openPencilSourceRestoreToken(
          store.openPencilSourceWorkspace?.bundle ?? null,
        ),
      );
  if (window.location.pathname === target) return;

  if (options?.replace) window.history.replaceState(null, "", target);
  else window.history.pushState(null, "", target);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
